/**
 * The aksjeeierbevis for the shares one holder took in one registered round.
 *
 * Printed rather than rendered to PDF, portalled out of the app, and offered in
 * Norwegian or English behind the flag — all three explained on `PrintSheet`,
 * which this shares with the kvittering.
 *
 * ## It certifies title, so it carries no money
 *
 * No kurs, no innbetalt, no payment date. An aksjeeierbevis says *these shares
 * are yours*; what they cost is the receipt's business, and a price printed here
 * invites the certificate to be filed as the tax document, which it is not.
 * (Payment still *gates* it — nothing unpaid is certified — it simply is not
 * printed.)
 *
 * ## Why the total is on it
 *
 * Bjarte chose this shape on 2 Sep over a bare per-round figure. The document is
 * per issuance, because that is what the Foretaksregisteret gate is per — so a
 * holder with shares from three rounds has three certificates, and one of them
 * naming 500 shares must not read as though 500 is everything they own. The
 * registered holding says what it is, and says which day it is true for.
 */
import { useState } from 'react'
import { fmtDateAbs, formatDate } from '@/shared/util/dates'
import { SHARE_CLASS, type Certificate, type DocLang } from '@/types/shareDocuments'
import { Field, Gaps, PrintSheet } from './PrintSheet'

const COPY = {
  nb: {
    heading: 'Aksjeeierbevis',
    orgNo: 'Org.nr.',
    holder: 'Aksjonær',
    name: 'Navn',
    address: 'Adresse',
    idCompany: 'Organisasjonsnummer',
    idPerson: 'Fødselsnummer',
    shares: 'Aksjene',
    countIn: (round: string) => `Aksjer tegnet i ${round}`,
    count: 'Antall aksjer',
    shareClass: 'Aksjeklasse',
    roundDate: 'Emisjonsdato',
    registered: 'Meldt Foretaksregisteret',
    holding: 'Samlet beholdning',
    holdingCount: 'Registrert som eier av',
    sharesUnit: 'aksjer',
    asOf: 'Per dato',
    foot: 'Aksjene er registrert på aksjonæren i selskapets aksjeeierbok.',
    issued: 'utstedt',
  },
  en: {
    heading: 'Share certificate',
    orgNo: 'Reg. no.',
    holder: 'Shareholder',
    name: 'Name',
    address: 'Address',
    idCompany: 'Organisation number',
    idPerson: 'National identity number',
    shares: 'The shares',
    countIn: (round: string) => `Shares subscribed in ${round}`,
    count: 'Number of shares',
    shareClass: 'Share class',
    roundDate: 'Date of issue',
    registered: 'Filed with the Register of Business Enterprises',
    holding: 'Total holding',
    holdingCount: 'Registered as owner of',
    sharesUnit: 'shares',
    asOf: 'As at',
    foot: 'These shares are registered to the shareholder in the company’s share register.',
    issued: 'issued',
  },
}

const nb = (n: number, lang: DocLang) => n.toLocaleString(lang === 'nb' ? 'nb-NO' : 'en-GB')
/** `03.09.2026` in Norwegian, `03 Sep 2026` in English — see `ShareReceipt`. */
const date = (iso: string, lang: DocLang) => (lang === 'nb' ? fmtDateAbs(iso) : formatDate(iso))

interface Props {
  certificate: Certificate
  /** ISO. The day the document itself is produced, for the closing line. */
  issuedOn: string
  onClose: () => void
}

export function ShareCertificate({ certificate: c, issuedOn, onClose }: Props) {
  const [lang, setLang] = useState<DocLang>('nb')
  const t = COPY[lang]

  return (
    <PrintSheet label={t.heading} lang={lang} onLang={setLang} onClose={onClose}>
      <Gaps missing={c.missing} lang={lang} />

      <header className="kvitt__head">
        <div className="kvitt__co">
          <strong>{c.companyName || '—'}</strong>
          <span>
            {t.orgNo} {c.companyOrgNumber || '—'}
          </span>
        </div>
        <h1 className="kvitt__title">{t.heading}</h1>
      </header>

      <section className="kvitt__block">
        <div className="kvitt__blocktitle">{t.holder}</div>
        <dl className="kvitt__fields">
          <Field label={t.name}>{c.holderName || '—'}</Field>
          <Field label={t.address}>{c.holderAddress || '—'}</Field>
          <Field label={c.holderIsCompany ? t.idCompany : t.idPerson}>
            {c.holderIdNumber || '—'}
          </Field>
        </dl>
      </section>

      <section className="kvitt__block">
        <div className="kvitt__blocktitle">{t.shares}</div>
        <dl className="kvitt__fields">
          <Field label={c.roundName ? t.countIn(c.roundName) : t.count}>
            <strong>{nb(c.shares, lang)}</strong>
          </Field>
          <Field label={t.shareClass}>{SHARE_CLASS[lang]}</Field>
          {c.roundDate && <Field label={t.roundDate}>{date(c.roundDate, lang)}</Field>}
          {/* The gate, printed. The increase takes effect on registration, so
              this date is why the document may exist at all. */}
          <Field label={t.registered}>
            <strong>{date(c.registeredAt, lang)}</strong>
          </Field>
        </dl>
      </section>

      {/*
        Their whole position, where it is known.

        Left out rather than printed as a zero when the cap table has not been
        rebuilt for this holder yet: a certificate saying "registrert beholdning
        0 aksjer" beside a round it certifies 500 of is worse than one that only
        certifies the round.
      */}
      {c.total.shares != null && (
        <section className="kvitt__block">
          <div className="kvitt__blocktitle">{t.holding}</div>
          <dl className="kvitt__fields">
            <Field label={t.holdingCount}>
              <strong>
                {nb(c.total.shares, lang)} {t.sharesUnit}
              </strong>
            </Field>
            <Field label={t.asOf}>{date(c.total.asOf, lang)}</Field>
          </dl>
        </section>
      )}

      <p className="kvitt__foot">{t.foot}</p>

      <p className="kvitt__sign">
        {c.companyName || '—'} · {t.issued} {date(issuedOn, lang)}
      </p>
    </PrintSheet>
  )
}
